import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth, storedUsername } from '../context/AuthContext';
import { buildApiUrl } from '../config/api';
import BaseLayout from './BaseLayout';
import ReadinessCard from './training/ReadinessCard';
import CoachInvitationsCard from './CoachInvitationsCard';
import * as snapshot from './training/sessionSnapshot';
import { runPrefetch, isUnreachable } from './training/prefetch';
import CalendarSubscriptionCard from './CalendarSubscriptionCard';
import { apiErrorMessage } from './prescriptionForm';
import {
  dueToday, nextUp, missed, dateLabel, todayISO,
} from './schedule';
import {
  describeSource,
  lastTrained,
  volumeLabel,
  loggedVolumeLabel,
  upNext,
  sinceLabel,
  sessionsByPhase,
  unseenBlocks,
  newBlockLine,
} from './athleteHome';
import '../styles/MyTrainingHome.css';

// What an athlete opens the app to see.
//
// # THE ORGANISING QUESTION
//
// "What do I train, and where do I start." Everything else is subordinate to
// getting a thumb onto a Train button in as few decisions as possible — this is
// read standing in a gym, on a phone, between sets.
//
// Until this existed, GET /assigned had no caller at all: a programme a coach
// assigned was reachable only by hunting through the programme list, and a squad
// calendar was not reachable at all.

// What is due, when there is a calendar to say.
//
// Renders nothing at all when there is no schedule — an athlete with no
// assignment start date has none, and an empty "Today" heading would read as a
// rest day rather than as an absent calendar.
/**
 * What the athlete last did — the acknowledgement this screen never made.
 *
 * # WHY IT IS HERE AT ALL
 *
 * An athlete who logged a full session came back to a screen that said nothing
 * about it. Four consecutive screens told a first-time user their work had not
 * counted, and this was the one with no data behind it rather than a bug in
 * front of it: the calendar answers what is COMING and nothing answered what
 * had happened.
 *
 * # WHY IT DOES NOT ALSO SAY WHAT IS NEXT
 *
 * Because the Calendar directly below does, in more detail and with the Train
 * button on it. Repeating the next session here would put the same sentence on
 * screen twice and push the calendar further down the phone.
 *
 * # WHY IT RENDERS NOTHING BEFORE THE FIRST SESSION
 *
 * A brand-new athlete has no last session, and the screen already has an empty
 * state built for them — the generator itself. A card saying "you have not
 * trained yet" above it would be a second empty state stacked on the first, and
 * it would greet someone on day one with an absence.
 */
const LastSession = ({ rollup }) => {
  if (!rollup) return null;

  return (
    <section className="card athlete-last-session">
      <p className="kicker">Last session · {sinceLabel(rollup.date)}</p>
      <p className="section-title athlete-last-session-name">{rollup.sessionName}</p>
      <p className="meta-copy">{loggedVolumeLabel(rollup)}</p>
    </section>
  );
};

/**
 * "New block from your coach" — one card per block the athlete has not opened.
 *
 * # WHY IT EXISTS
 *
 * A coach published a block and nothing told the athlete; they found it by
 * noticing a new section on this screen, often days later. The email covers
 * the athlete who is not in the app. This covers the one who is, and it does
 * not depend on mail having been delivered — or switched on.
 *
 * # WHEN IT GOES AWAY
 *
 * When the athlete opens the block (from here or from any of its sessions
 * below) or says "Got it". Both record SeenAt on the server, so the card does
 * not come back on the next device or the next load.
 */
const NewBlockCard = ({ assignment, onOpen, onDismiss }) => (
  <section className="card athlete-new-block" aria-label="New block from your coach">
    <p className="kicker">New block from your coach</p>
    <p className="section-title athlete-new-block-name">{assignment.programName}</p>
    <p className="meta-copy">{newBlockLine(assignment)}</p>
    <div className="athlete-new-block-actions">
      <a
        className="btn-primary"
        href={`#block-${assignment.id}`}
        onClick={() => onOpen(assignment)}
      >
        Open it
      </a>
      <button type="button" className="btn-ghost" onClick={() => onDismiss(assignment)}>
        Got it
      </button>
    </div>
  </section>
);

// `savedCopy` is set when these entries are the copy saved on the phone rather
// than the server's answer. Each of today's sessions then says whether it will
// open without a signal, so the athlete finds out here and not on the gym floor.
const Calendar = ({ entries, savedCopy, hasCopy }) => {
  if (!entries || entries.length === 0) return null;

  const today = todayISO();
  const now = dueToday(entries, today);
  const next = nextUp(entries, today);
  // Not from a saved copy: its lastTrainedOn is as old as the copy, so a
  // session trained since would be reported as skipped.
  const skipped = savedCopy ? [] : missed(entries, today);

  return (
    <section className="card athlete-today">
      {now.length > 0 ? (
        <>
          <h2 className="section-title">Today</h2>
          <ul className="athlete-sessions">
            {now.map((e) => (
              <li key={`${e.sessionId}-${e.date}`}>
                <Link to={`/programs/${e.programId}/sessions/${e.sessionId}/train`}>
                  <span className="athlete-session-name">{e.sessionName}</span>
                  <span className="athlete-session-meta muted">
                    {e.programName} · week {e.week}
                  </span>
                  <span className="athlete-session-when athlete-due">Due</span>
                  {savedCopy && (
                    <span className="athlete-session-offline muted">
                      {hasCopy(e) ? 'Saved for offline' : 'Needs a signal to open'}
                    </span>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <>
          <h2 className="section-title">Nothing due today</h2>
          {next && (
            <p className="meta-copy">
              Next: <strong>{next.sessionName}</strong>{' '}
              {dateLabel(next.date, today)}
            </p>
          )}
        </>
      )}

      {/* A session is missed only when nothing was logged against it on or
          after the day it was due — training on Tuesday instead of Monday is
          not a failure and must not be reported as one. */}
      {skipped.length > 0 && (
        <p className="meta-copy athlete-missed">
          {skipped.length} session{skipped.length === 1 ? '' : 's'} not yet done
          from earlier: {skipped.slice(0, 3).map((e) => e.sessionName).join(', ')}
        </p>
      )}
    </section>
  );
};

const MyTrainingHome = () => {
  const { token, username } = useAuth();

  const [assignments, setAssignments] = useState([]);
  const [schedule, setSchedule] = useState([]);
  // When the calendar on screen is the copy saved on this phone (no network),
  // the time it was saved; null when it is the server's answer.
  const [scheduleSavedAt, setScheduleSavedAt] = useState(null);
  const [lastSession, setLastSession] = useState(null);
  const [programs, setPrograms] = useState({});
  const [phases, setPhases] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Whether this server can write a programme at all. The self-serve routes are
  // mounted only when generation is configured, so a 404 here is the answer —
  // and offering a button that 404s is worse than not offering one.
  const [canWrite, setCanWrite] = useState(false);

  // Whose training the state above holds. Everything on this screen belongs to
  // one athlete; when another signs in (another tab, the same phone), the first
  // one's calendar and blocks must not stay on screen while the second one's
  // load is in flight — nor seed a prefetch with the second one's token.
  const [loadedFor, setLoadedFor] = useState(username);

  const authConfig = useCallback(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  // No network: the calendar this athlete's phone last saw, labelled as such.
  // Returns whether there was one.
  const fallBackToSavedSchedule = useCallback(() => {
    const saved = snapshot.loadSchedule(username);
    if (!saved) return false;
    setSchedule(saved.data);
    setScheduleSavedAt(saved.savedAt);
    return true;
  }, [username]);

  // `signal` is aborted on unmount and on any change of user or token. Every
  // await is followed by a check, because a response that lands after logout
  // would otherwise re-save a calendar logout has just cleared, and one that
  // lands after an account switch would show (and save) the previous
  // athlete's schedule on the next one's screen. storedUsername moves the
  // moment logout runs, before React gets round to aborting.
  const load = useCallback(async (signal) => {
    const gone = () => signal.aborted || Boolean(username && storedUsername() !== username);
    const get = (path) => axios.get(buildApiUrl(path), { ...authConfig(), signal });

    // A fresh start for whoever this load is for. Only on mount and on a change
    // of user or token, so it costs no flicker on an ordinary visit.
    setLoadedFor(username);
    setLoading(true);
    setError(null);
    setAssignments([]);
    setSchedule([]);
    setScheduleSavedAt(null);
    setLastSession(null);
    setPrograms({});
    setPhases({});
    setCanWrite(false);
    let assigned;
    try {
      assigned = await get('/assigned');
    } catch (err) {
      if (gone()) return;
      // Offline, the screen used to be one red line and nothing to press.
      // Today's session may well be saved on the phone (training/prefetch.js),
      // so offer the saved calendar instead. A refusal is still an error.
      if (!(isUnreachable(err) && fallBackToSavedSchedule())) {
        setError(apiErrorMessage(err, 'Could not load your training.'));
      }
      setLoading(false);
      return;
    }

    if (gone()) return;
    try {
      const { data } = assigned;
      const list = data?.assignments ?? [];
      setAssignments(list);

      // Can this server write a programme? A 204 means "you have none in
      // flight", which still means yes; only a 404 — the routes not being
      // mounted — means no. Settled separately from the calendar because
      // neither is a precondition for the other.
      try {
        await get('/self-programs');
        if (gone()) return;
        setCanWrite(true);
      } catch (err) {
        if (gone()) return;
        setCanWrite(err.response?.status !== 404);
      }

      // The calendar is an addition, not a precondition: an athlete whose
      // assignment has no start date has no schedule, and the block list below
      // is still their training.
      try {
        const cal = await get('/schedule');
        if (gone()) return;
        const entries = cal.data?.entries ?? [];
        setSchedule(entries);
        snapshot.saveSchedule(username, entries);
      } catch (err) {
        if (gone()) return;
        if (!(isUnreachable(err) && fallBackToSavedSchedule())) setSchedule([]);
      }

      // Likewise the last session. 204 means "you have not trained yet", which
      // is a real state and not a failure — axios gives it to us with no body,
      // so the falsy check covers both it and a server that does not have the
      // route at all. Losing this must not cost the athlete their calendar.
      try {
        const last = await get('/training/last-session');
        if (gone()) return;
        setLastSession(last.data || null);
      } catch {
        if (gone()) return;
        setLastSession(null);
      }

      // Each assignment names a programme; the sessions live on the programme.
      // Fetched in parallel rather than in sequence — an athlete with four
      // blocks should not wait four round trips.
      const ids = [...new Set(list.map((a) => a.programId))];
      const loaded = await Promise.all(ids.map(async (id) => {
        // Settled independently, not Promise.all: the phase list is only used
        // to NAME the blocks, so losing it must not cost the athlete the
        // sessions. Failing both together is exactly what happened when the
        // phase list was owner-only — the programme loaded and the screen still
        // said it could not be read.
        const [program, phase] = await Promise.allSettled([
          get(`/programs/${id}`),
          get(`/programs/${id}/phases`),
        ]);
        return [
          id,
          program.status === 'fulfilled' ? program.value.data : null,
          phase.status === 'fulfilled' ? (phase.value.data?.phases ?? []) : [],
        ];
      }));
      if (gone()) return;

      setPrograms(Object.fromEntries(loaded.map(([id, p]) => [id, p])));
      setPhases(Object.fromEntries(loaded.map(([id, , ph]) => [id, ph])));
    } catch (err) {
      if (!gone()) setError(apiErrorMessage(err, 'Could not load your training.'));
    } finally {
      if (!gone()) setLoading(false);
    }
  }, [authConfig, username, fallBackToSavedSchedule]);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  // Save the week's sessions for a gym with no signal (training/prefetch.js).
  //
  // Only from the server's calendar, never from a saved one, and only online.
  // The cleanup aborts on unmount and on any change of user or token, which is
  // what keeps one athlete's prefetch from landing under the next one's keys.
  useEffect(() => {
    if (!username || !token || loadedFor !== username) return undefined;
    if (scheduleSavedAt !== null || schedule.length === 0) return undefined;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return undefined;

    const controller = new AbortController();
    runPrefetch({
      user: username,
      entries: schedule,
      signal: controller.signal,
      isCurrent: () => storedUsername() === username,
      fetchTrain: (programId, sessionId, signal) => axios
        .get(buildApiUrl(`/programs/${programId}/sessions/${sessionId}/train`), {
          headers: { Authorization: `Bearer ${token}` },
          signal,
        })
        .then((res) => res.data),
      // The block's weeks, so the logger's week selector works offline too.
      fetchPhases: (programId, signal) => axios
        .get(buildApiUrl(`/programs/${programId}/phases`), {
          headers: { Authorization: `Bearer ${token}` },
          signal,
        })
        .then((res) => res.data?.phases ?? []),
    });
    return () => controller.abort();
  }, [schedule, scheduleSavedAt, username, token, loadedFor]);

  const hasCopy = useCallback(
    (e) => Boolean(snapshot.load(username, e.programId, e.sessionId)),
    [username]
  );

  // `loadedFor !== username` covers the one commit between a change of user
  // and the load effect's reset, which can paint. jsdom flushes effects inside
  // act(), so no test can see that frame; OFFLINE-19 covers everything after.

  // Marks a block seen. The card goes at once — waiting on the round trip
  // would leave it on screen under the athlete's thumb — and comes back if the
  // server did not record it, because a card that vanished here and reappears
  // on the next load is more confusing than one that stays.
  const markSeen = useCallback(async (assignment) => {
    if (!assignment?.publishedAt || assignment.seenAt) return;
    const stamp = new Date().toISOString();
    const setSeen = (value) => setAssignments((list) => list.map((a) => (
      a.id === assignment.id ? { ...a, seenAt: value } : a
    )));
    setSeen(stamp);
    try {
      const { data } = await axios.post(
        buildApiUrl(`/assigned/${assignment.id}/seen`), null, authConfig(),
      );
      if (data?.seenAt) setSeen(data.seenAt);
    } catch {
      setSeen(null);
    }
  }, [authConfig]);

  if (loading || loadedFor !== username) {
    return <BaseLayout><p className="athlete-status">Loading your training…</p></BaseLayout>;
  }

  return (
    <BaseLayout>
      <div className="athlete-home">
        <header className="athlete-header">
          <span className="pill">Your training</span>
          <h1 className="page-title">What&apos;s on</h1>
        </header>

        {error && <p className="status-error" role="alert">{error}</p>}

        {/* First, because it is a question only the athlete can answer and
            nothing else here depends on it. Renders nothing when there is no
            invitation, which is nearly always. */}
        <CoachInvitationsCard />

        {/* News from the coach, before anything about today: a block that
            starts tomorrow is the thing this athlete most needs to know. */}
        {!error && unseenBlocks(assignments).map((assignment) => (
          <NewBlockCard
            key={`new-${assignment.id}`}
            assignment={assignment}
            onOpen={markSeen}
            onDismiss={markSeen}
          />
        ))}

        {/* What just happened, then what to answer before training, then what is
            on. ReadinessCard's own rule — above the calendar, never below the
            day's sessions — is unchanged by putting a one-line acknowledgement
            in front of it. */}
        {!error && <LastSession rollup={lastSession} />}

        {/* Above the calendar on purpose. It is answered BEFORE training, and a
            card below the day's sessions is one an athlete scrolls past on their
            way to the Train button — which is the whole journey this screen is
            built around. */}
        <ReadinessCard />

        {scheduleSavedAt !== null && (
          <p className="athlete-offline meta-copy" role="status">
            Offline: this is the saved copy of your schedule, from{' '}
            {snapshot.describeSavedAt(scheduleSavedAt).replace(/^(at|on) /, '')}.
          </p>
        )}

        {!error && (
          <Calendar entries={schedule} savedCopy={scheduleSavedAt !== null} hasCopy={hasCopy} />
        )}

        {/* Not when offline on a saved calendar: there the list is empty for
            want of a network, and "nothing assigned" would be a lie. */}
        {!error && scheduleSavedAt === null && assignments.length === 0 && (
          <div className="card athlete-empty">
            {/* Somebody with no coach is the person this screen exists for, so
                the generator IS the empty state rather than a footnote to it.
                The inline link that used to sit above this button pointed at
                /create-program — the coach's manual builder — which is the last
                place to send someone who has just asked to be given a
                programme. */}
            <h2 className="section-title">
              {canWrite ? 'Start with a programme' : 'Nothing assigned yet'}
            </h2>
            <p className="meta-copy">
              {canWrite
                ? 'Answer a few questions about your equipment and the days you '
                  + 'train, and one will be written for you. If a coach assigns '
                  + 'you a programme later, it turns up here too.'
                : 'When a coach assigns you a programme or adds you to a squad, '
                  + 'it appears here.'}
            </p>
            {canWrite && (
              <Link className="btn-primary athlete-write-me" to="/write-me-a-program">
                Write me a program
              </Link>
            )}
          </div>
        )}


        {assignments.map((assignment) => {
          const program = programs[assignment.programId];
          const source = describeSource(assignment.mode);
          if (!program) {
            return (
              <section key={assignment.id} className="card athlete-block">
                <h2 className="section-title">{assignment.programName}</h2>
                <p className="meta-copy">This programme could not be loaded.</p>
              </section>
            );
          }

          const grouped = sessionsByPhase(program, phases[assignment.programId]);
          const next = upNext(program.sessions);
          // Opening any session of a new block is opening the block.
          const opened = () => markSeen(assignment);

          return (
            <section key={assignment.id} id={`block-${assignment.id}`} className="card athlete-block">
              <div className="athlete-block-head">
                <div>
                  <h2 className="section-title">{program.name}</h2>
                  <p className="meta-copy athlete-source">
                    <span className="athlete-badge">{source.label}</span> {source.detail}
                  </p>
                </div>
              </div>

              {/* The primary action, above everything else. An athlete who does
                  nothing but press this trains the right session. */}
              {next && (
                <Link
                  className="athlete-next"
                  to={`/programs/${assignment.programId}/sessions/${next.id}/train`}
                  onClick={opened}
                >
                  <span className="athlete-next-label">Up next</span>
                  <span className="athlete-next-name">{next.name}</span>
                  <span className="athlete-next-meta">
                    {volumeLabel(next)} · {sinceLabel(lastTrained(next))}
                  </span>
                </Link>
              )}

              {grouped.map((bucket, i) => (
                <div key={bucket.phase?.id ?? `loose-${i}`} className="athlete-phase">
                  <h3 className="athlete-phase-name">
                    {bucket.phase
                      ? `Phase ${bucket.phase.phaseNumber} · ${bucket.phase.name}`
                      : 'Other sessions'}
                    {bucket.phase?.strengthQualityLabel && (
                      <span className="muted"> — {bucket.phase.strengthQualityLabel}</span>
                    )}
                    {bucket.phase && (
                      <Link
                        className="athlete-phase-grid"
                        to={`/programs/${assignment.programId}/phases/${bucket.phase.id}/grid`}
                      >
                        Progress
                      </Link>
                    )}
                  </h3>

                  <ul className="athlete-sessions">
                    {bucket.sessions.map((session) => (
                        <li key={session.id}>
                          <Link
                            to={`/programs/${assignment.programId}/sessions/${session.id}/train`}
                            onClick={opened}
                          >
                            <span className="athlete-session-name">{session.name}</span>
                            <span className="athlete-session-meta muted">
                              {volumeLabel(session)}
                            </span>
                            <span className="athlete-session-when muted">
                              {sinceLabel(lastTrained(session))}
                            </span>
                          </Link>
                        </li>
                    ))}
                  </ul>
                </div>
              ))}
            </section>
          );
        })}

        {/* Below the training, not above it. A second block is not what an
            athlete opened the app to do, and putting this between the day's
            sessions and the programme they train pushes the Train button down
            the screen for a link almost nobody wants on any given morning. */}
        {!error && assignments.length > 0 && canWrite && (
          <p className="meta-copy athlete-write-more">
            <Link to="/write-me-a-program">Write me another program</Link>
          </p>
        )}

        {/* The way back into their own training.
         *
         * Not gated on having LOGGED anything — the screen it opens has an
         * honest empty state, and checking first would cost a request on every
         * load of the busiest screen in the app to hide one line.
         *
         * But gated on having a programme. With no assignments this page IS the
         * generator: an empty state whose whole job is to get a first block
         * written, carrying its own primary button. A second link underneath it
         * competes with that, on the one screen where there is exactly one thing
         * to do — and somebody with no programme has no lifts to check anyway.
         *
         * A working history view has existed all along with nothing pointing at
         * it (docs/mvp-funnel-2026-09-21.md §4). An unreachable second one would
         * be the same bug twice. */}
        {!error && assignments.length > 0 && (
          <p className="meta-copy athlete-history-link">
            <Link to="/my-history">See how my lifts are going</Link>
          </p>
        )}

        {/* The calendar feed, last. It lives on the account card too, but an
            athlete with no roster never sees that card — and athletes are who
            the feed is for. Gated like the history link: with nothing assigned
            there is nothing on the calendar to subscribe to. */}
        {!error && assignments.length > 0 && (
          <div className="card athlete-calendar-feed">
            <CalendarSubscriptionCard />
          </div>
        )}
      </div>
    </BaseLayout>
  );
};

export default MyTrainingHome;
